/**
 * Device details — Device logs (update activity) tab using {@link ConfiguredDataTable}.
 * Column config lives in {@link DeviceUpdateLogsTableConfig}; column picker is hidden (fixed layout).
 */
import React, { useEffect, useState } from "react";
import { Box, Paper } from "@mui/material";
import { setAlert } from "./../../action/alert";
import { connect } from "react-redux";
import "./DeviceUpdateLogs.css";
import { getDeviceLogsData } from "./../../action/devicelogs";
import Alert from "../layout/Alert";
import ConfiguredDataTable from "../Common/DataTable/ConfiguredDataTable";
import EmptyState from "../Common/EmptyState/EmptyState";
import ScreenLoader from "../Common/ScreenLoader/ScreenLoader";
import {
  deviceUpdateLogRenderers,
  deviceUpdateLogsTableConfig,
  formatDeviceLogTimestamp,
} from "./DeviceUpdateLogsTableConfig";

const DeviceUpdateLogs = ({ deviceDetails, setCustomAlert }) => {
  const headers = {
    serialNumber: "S.No",
    activity: "Actions",
    timestamp: "Timestamp",
    productSerialNumber: "Product Serial Number",
    macAddress: "MAC Address",
    status: "Status",
  };

  const [deviceLogsData, setDeviceLogsData] = useState([]);
  const [updateFlag, setUpdateFlag] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const updateDeviceLogsData = (resolveFunc, errFunc) => {
    setIsLoading(true);
    getDeviceLogsData(deviceDetails?.deviceId ?? "")
      .then((res) => {
        setIsLoading(false);
        if (!resolveFunc) {
          setDeviceLogsData(res);
        } else {
          resolveFunc(res);
        }
      })
      .catch((err) => {
        setIsLoading(false);
        if (!errFunc) {
          console.log(err);
          setCustomAlert("Error while fetching device logs data.", "danger");
          setDeviceLogsData([]);
        } else {
          errFunc(err);
        }
      });
  };

  useEffect(() => {
    if (
      deviceDetails &&
      deviceDetails instanceof Object &&
      deviceDetails.hasOwnProperty("deviceId")
    ) {
      updateDeviceLogsData();
    }
  }, [deviceDetails]);

  useEffect(() => {
    if (updateFlag) {
      updateDeviceLogsData();
    }
  }, [updateFlag]);

  const deviceUpdateLogRows = deviceLogsData.map((element, index) => ({
    ...element,
    rowId: `${element?.timestamp || "log"}-${index}`,
    serialNumber: index + 1,
  }));
  const hasDeviceLogData = Array.isArray(deviceUpdateLogRows) && deviceUpdateLogRows.length > 0;

  if (isLoading) {
    return (
      <ScreenLoader
        height="40vh"
        size={60}
        sx={{ mt: 2, mb: 2, py: 2, minHeight: "40vh" }}
      />
    );
  }

  return (
    <Box sx={{ width: "100%", minWidth: 0, mt: 2, mb: 2 }}>
      <Alert />
      <Paper
        sx={{
          p: 2,
          boxShadow: 2,
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        {!hasDeviceLogData ? (
          <EmptyState
            message="No Data found"
            sx={{ bgcolor: "transparent", p: 0 }}
          />
        ) : (
          <ConfiguredDataTable
            config={deviceUpdateLogsTableConfig}
            headers={Object.fromEntries(
              Object.entries(headers).map(([key, label]) => [key, { header: label }])
            )}
            rows={deviceUpdateLogRows}
            getCellDisplayValue={(row, key) => {
              if (key === "serialNumber") return String(row.serialNumber || "");
              // Align filter text with on-screen timestamp formatting (see DeviceUpdateLogsTableConfig).
              if (key === "timestamp") return formatDeviceLogTimestamp(row.timestamp);
              return String(row?.[key] ?? "");
            }}
            getCellSortValue={(row, key) => {
              if (key === "timestamp") return Number(new Date(row?.timestamp || 0).getTime() || 0);
              return String(row?.[key] ?? "").toLowerCase();
            }}
            renderCell={({ column, value, row }) => {
              const renderType = deviceUpdateLogsTableConfig.columns[column.key]?.renderType || "text";
              const renderer = deviceUpdateLogRenderers[renderType] || deviceUpdateLogRenderers.text;
              return renderer(value, row, column.key);
            }}
            defaultHeaderCellSx={{
              fontWeight: 600,
              backgroundColor: "background.paper",
              whiteSpace: "nowrap",
            }}
            controlsProps={{ showColumnsButton: false }}
            dataTableProps={{
              rowKey: "rowId",
              minWidth: "100%",
              maxHeight: "58vh",
              striped: false,
              hoverEffect: false,
              tableContainerSx: { boxShadow: "none", borderRadius: 0, width: "100%", minWidth: 0 },
              sx: { scrollBehavior: "auto" },
              msgForDisplay: "No Data found",
            }}
          />
        )}
      </Paper>
    </Box>
  );
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => ({
  setCustomAlert: (message, alertType) =>
    dispatch(setAlert(message, alertType)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DeviceUpdateLogs);
